import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../utils/cn";

/**
 * ボタンのバリアント定義（CVA）
 */
const buttonVariants = cva(
    /* 共通スタイル */
    [
        "inline-flex items-center justify-center gap-2 font-medium",
        "transition-all duration-200 ease-in-out",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 active:ring-0",
        "active:scale-[0.98]",
        "disabled:cursor-not-allowed disabled:active:scale-100",
    ],
    {
        variants: {
            /** ボタンの外観 */
            variant: {
                primary: [
                    "bg-brand-500 text-white",
                    "enabled:hover:bg-brand-600 active:bg-brand-700",
                    "disabled:bg-gray-300 disabled:text-gray-500",
                ],
                secondary: [
                    "bg-[#D9D9D9] text-gray-600",
                    "enabled:hover:bg-gray-400 active:bg-gray-500",
                    "disabled:bg-gray-100 disabled:text-gray-400",
                ],
                outline: [
                    "bg-transparent border-2 border-brand-500 text-brand-500",
                    "enabled:hover:bg-brand-50 active:bg-brand-100",
                    "disabled:border-gray-300 disabled:text-gray-400",
                ],
                ghost: [
                    "bg-transparent text-brand-500",
                    "enabled:hover:bg-brand-50 active:bg-brand-100",
                    "disabled:text-gray-400",
                ],
                danger: [
                    "bg-error-500 text-white",
                    "enabled:hover:bg-error-600 active:bg-error-700",
                    "disabled:bg-gray-300 disabled:text-gray-500",
                ],
            },
            /** ボタンのサイズ */
            size: {
                sm: "px-3 py-1.5 text-sm rounded-[5px]",
                md: "px-4 py-2.5 text-base rounded-lg",
                lg: "px-6 py-3.5 text-lg rounded-lg",
            },
            /** アイコンのみモード */
            iconOnly: {
                true: "",
                false: "",
            },
            /** フル幅 */
            fullWidth: {
                true: "w-full",
                false: "",
            },
        },
        compoundVariants: [
            /* アイコンのみ × サイズ：パディングを正方形に上書き */
            { iconOnly: true, size: "sm", className: "p-1.5" },
            { iconOnly: true, size: "md", className: "p-2" },
            { iconOnly: true, size: "lg", className: "p-3" },
        ],
        defaultVariants: {
            variant: "primary",
            size: "md",
            iconOnly: false,
            fullWidth: false,
        },
    }
);

/**
 * Button コンポーネントのProps
 */
export interface ButtonProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
        Omit<VariantProps<typeof buttonVariants>, "iconOnly" | "fullWidth" | "rounded"> {
    /** フル幅 */
    fullWidth?: boolean;
    /** ローディング状態 */
    isLoading?: boolean;
    /** 無効状態 */
    disabled?: boolean;
    /** 左側アイコン */
    leftIcon?: ReactNode;
    /** 右側アイコン */
    rightIcon?: ReactNode;
    /** アイコンのみモード（子要素をアイコンとして扱う） */
    iconOnly?: boolean;
    /** 子要素 */
    children: ReactNode;
}

/**
 * Button - ボタンコンポーネント
 *
 * @description
 * 活性/非活性、バリアント、サイズ、角丸、アニメーションをサポート
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant,
            size,
            fullWidth = false,
            isLoading = false,
            disabled = false,
            leftIcon,
            rightIcon,
            iconOnly = false,
            children,
            className,
            onClick,
            ...props
        },
        ref
    ) => {
        const isDisabled = disabled || isLoading;

        const handleMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
        };

        const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
            onClick?.(e);
        };

        return (
            <button
                ref={ref}
                disabled={isDisabled}
                className={cn(
                    buttonVariants({ variant, size, iconOnly, fullWidth }),
                    className
                )}
                {...props}
                onMouseDown={isDisabled ? undefined : handleMouseDown}
                onClick={isDisabled ? undefined : handleClick}
            >
                {/* ローディングスピナー */}
                {isLoading && (
                    <LoadingSpinner className="w-4 h-4 animate-spin" />
                )}

                {/* アイコンのみモード */}
                {iconOnly && !isLoading && (
                    <span className="flex-shrink-0 w-5 h-5">{children}</span>
                )}

                {/* 通常モード */}
                {!iconOnly && (
                    <>
                        {/* 左アイコン */}
                        {!isLoading && leftIcon && (
                            <span className="flex-shrink-0 flex items-center">{leftIcon}</span>
                        )}

                        {/* ボタンテキスト */}
                        <span className="leading-none">{children}</span>

                        {/* 右アイコン */}
                        {!isLoading && rightIcon && (
                            <span className="flex-shrink-0 flex items-center">{rightIcon}</span>
                        )}
                    </>
                )}
            </button>
        );
    }
);

Button.displayName = "Button";

// ローディングスピナー
const LoadingSpinner = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        className={className}
    >
        <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
        />
        <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
    </svg>
);

export default Button;
